/* global google */
import { useEffect, useRef, useState } from 'react';
import braintree from 'braintree-web';
import styles from './GooglePayButton.module.sass';

const GooglePayButton = ({ clientToken, checkoutTotal, onApprove, orderData, offer }) => {
  const googlePaymentInstanceRef = useRef(null);
  const paymentsClientRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const buttonContainerRef = useRef(null);

  useEffect(() => {
    // Check if Google Pay script is loaded
    if (typeof google === 'undefined' || !google.payments) {
      console.error('Google Pay script not loaded');
      return;
    }

    if (!clientToken) {
      return;
    }

    braintree.client.create(
      {
        authorization: clientToken,
      },
      (clientErr, clientInstance) => {
        if (clientErr) {
          console.error('Error creating Braintree client:', clientErr);
          return;
        }

        const googlePayMerchantId = offer.googlePayMerchantId || '';

        braintree.googlePayment.create(
          {
            client: clientInstance,
            googlePayVersion: 2,
            googleMerchantId: googlePayMerchantId,
          },
          (googlePaymentErr, googlePaymentInstance) => {
            if (googlePaymentErr) {
              console.error('Error creating Google Payment instance:', googlePaymentErr);
              return;
            }

            googlePaymentInstanceRef.current = googlePaymentInstance;

            // Use TEST environment on localhost, PRODUCTION on live domains
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const paymentsClient = new google.payments.api.PaymentsClient({
              environment: isLocalhost ? 'TEST' : 'PRODUCTION',
            });

            paymentsClientRef.current = paymentsClient;

            // Use Braintree SDK to create the isReadyToPay request
            // This ensures correct tokenization specification is generated
            const readyToPayRequest = googlePaymentInstance.createPaymentDataRequest({
              transactionInfo: {
                currencyCode: offer.currency || 'USD',
                totalPriceStatus: 'ESTIMATED',
                totalPrice: '0.01',
              },
            });

            // For isReadyToPay, we only need basic card info, not full tokenization
            paymentsClient
              .isReadyToPay({
                apiVersion: readyToPayRequest.apiVersion,
                apiVersionMinor: readyToPayRequest.apiVersionMinor,
                allowedPaymentMethods: readyToPayRequest.allowedPaymentMethods,
              })
              .then((response) => {
                if (response.result) {
                  setIsReady(true);
                  
                  // Create and append the Google Pay button
                  if (buttonContainerRef.current) {
                    const button = paymentsClient.createButton({
                      onClick: handleGooglePayClick,
                      buttonColor: 'black',
                      buttonType: 'plain',
                      buttonSizeMode: 'fill',
                    });
                    buttonContainerRef.current.innerHTML = '';
                    buttonContainerRef.current.appendChild(button);
                  }
                }
              })
              .catch((err) => {
                console.error('Error checking Google Pay readiness:', err);
              });
          }
        );
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientToken, offer]);

  const handleGooglePayClick = () => {
    const googlePaymentInstance = googlePaymentInstanceRef.current;
    const paymentsClient = paymentsClientRef.current;

    if (!googlePaymentInstance || !paymentsClient) {
      console.error('Google Pay not ready');
      return;
    }

    const merchantName = offer.googlePayMerchantName || offer.brand || 'Store';
    const googlePayMerchantId = offer.googlePayMerchantId || '';

    // Use Braintree SDK's createPaymentDataRequest - it handles tokenization correctly
    const paymentDataRequest = googlePaymentInstance.createPaymentDataRequest({
      transactionInfo: {
        currencyCode: offer.currency || 'USD',
        totalPriceStatus: 'FINAL',
        totalPrice: String(checkoutTotal),
      },
      merchantInfo: {
        merchantName: merchantName,
        merchantId: googlePayMerchantId,
      },
      emailRequired: true,
      shippingAddressRequired: true,
      shippingAddressParameters: {
        phoneNumberRequired: true,
      },
    });

    // Override the payment methods to include billing address
    if (paymentDataRequest.allowedPaymentMethods && paymentDataRequest.allowedPaymentMethods[0]) {
      paymentDataRequest.allowedPaymentMethods[0].parameters.billingAddressRequired = true;
      paymentDataRequest.allowedPaymentMethods[0].parameters.billingAddressParameters = {
        format: 'FULL',
        phoneNumberRequired: true,
      };
    }

    paymentsClient
      .loadPaymentData(paymentDataRequest)
      .then((paymentData) => {
        // Parse the Google Pay response
        googlePaymentInstance.parseResponse(paymentData, (err, result) => {
          if (err) {
            console.error('Error parsing Google Pay response:', err);
            return;
          }

          // Extract shipping address from Google Pay response
          const shippingAddress = paymentData.shippingAddress || {};
          const email = paymentData.email || '';

          // Parse the name (Google Pay provides full name, need to split)
          const fullName = shippingAddress.name || '';
          const nameParts = fullName.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          // Build complete order data with all info from Google Pay
          const newOrderData = {
            ...orderData,
            shippingCity: shippingAddress.locality || '',
            shippingAddress1: shippingAddress.address1 || '',
            shippingAddress2: shippingAddress.address2 || '',
            firstName: firstName,
            lastName: lastName,
            email: email,
            shippingZip: shippingAddress.postalCode || '',
            shippingState: shippingAddress.administrativeArea || '',
            shippingCountry: shippingAddress.countryCode?.toUpperCase() || 'US',
            phone: shippingAddress.phoneNumber?.replace(/^\+1/, '').replace(/\s/g, '') || '',
            braintree_nonce: result.nonce,
            creditCardType: 'googlepay',
          };

          onApprove(newOrderData);
        });
      })
      .catch((err) => {
        if (err.statusCode === 'CANCELED') {
          // eslint-disable-next-line no-console
          console.log('Google Pay cancelled by user');
        } else {
          // eslint-disable-next-line no-console
          console.error('Error loading Google Pay payment data:', err);
        }
      });
  };

  if (!isReady) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div ref={buttonContainerRef} className={styles.buttonWrapper} />
    </div>
  );
};

export default GooglePayButton;
